import { normalizeWord } from "./spellUtils";

// Main-thread side of the spell checker: one shared worker, results cached.

const PERSONAL_KEY = "iskool_spell_personal_words";

let worker = null;
let readyPromise = null;
let seq = 0;
const pending = new Map();
const verdicts = new Map(); // normalized word -> true when misspelled
const suggestionCache = new Map();
const ignoredThisSession = new Set();
let personal = null;
const listeners = new Set();

function call(message) {
  return new Promise((resolve, reject) => {
    const id = ++seq;
    pending.set(id, { resolve, reject });
    worker.postMessage({ ...message, id });
  });
}

/** Loads the worker and dictionaries once; rejects if they cannot load. */
export function ensureSpellChecker() {
  if (!readyPromise) {
    readyPromise = new Promise((resolve, reject) => {
      try {
        worker = new Worker(new URL("./spell.worker.js", import.meta.url));
      } catch (error) {
        reject(error);
        return;
      }
      worker.onmessage = ({ data }) => {
        const entry = pending.get(data.id);
        if (!entry) return;
        pending.delete(data.id);
        if (data.error) entry.reject(new Error(data.error));
        else entry.resolve(data.result);
      };
      worker.onerror = (event) => reject(event.error || new Error("spell worker"));
      call({ type: "init", base: process.env.PUBLIC_URL || "" }).then(
        resolve,
        reject,
      );
    }).catch((error) => {
      readyPromise = null; // allow a retry on the next focus
      throw error;
    });
  }
  return readyPromise;
}

function personalWords() {
  if (!personal) {
    personal = new Set();
    try {
      const raw = window.localStorage.getItem(PERSONAL_KEY);
      (raw ? JSON.parse(raw) : []).forEach((w) => personal.add(w));
    } catch (e) {
      /* storage unavailable: the list just lives for this page */
    }
  }
  return personal;
}

const isUserApproved = (norm) =>
  personalWords().has(norm) || ignoredThisSession.has(norm);

/** Set of normalized words (from `words`) that are misspelled. */
export async function findMisspelled(words) {
  await ensureSpellChecker();
  const unknown = words.filter((w) => !verdicts.has(w));
  if (unknown.length) {
    const bad = new Set(await call({ type: "check", words: unknown }));
    unknown.forEach((w) => verdicts.set(w, bad.has(w)));
  }
  return new Set(words.filter((w) => verdicts.get(w) && !isUserApproved(w)));
}

/** True when we already know `word` (normalized) is spelled correctly. */
export const isKnownCorrect = (normalized) =>
  verdicts.has(normalized) && verdicts.get(normalized) === false;

export async function suggestFor(word) {
  await ensureSpellChecker();
  const key = normalizeWord(word);
  if (!suggestionCache.has(key)) {
    suggestionCache.set(key, await call({ type: "suggest", word }));
  }
  return suggestionCache.get(key);
}

function notify() {
  listeners.forEach((fn) => fn());
}

/** Subscribe to "ignore / add to dictionary" changes so fields re-check. */
export function onDictionaryChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function ignoreWord(word) {
  ignoredThisSession.add(normalizeWord(word));
  notify();
}

export function addToPersonalDictionary(word) {
  const set = personalWords();
  set.add(normalizeWord(word));
  try {
    window.localStorage.setItem(PERSONAL_KEY, JSON.stringify([...set]));
  } catch (e) {
    /* kept for this page only */
  }
  notify();
}
